<template>
  <div
    class="bp1440-card-slider bp1680-card-slider relative mx-auto block w-full sm:max-w-110 md:max-w-182 lg:max-w-214.5 xl:max-w-281.25 2xl:max-w-331.25"
  >
    <!-- Static overlay for navigation arrows so they don't slide -->
    <div
      v-if="slides.length > 1"
      class="pointer-events-none absolute inset-0 z-10 w-full px-5 pt-5 pb-10 md:px-7.5 md:pt-7.5 md:pb-22.5 xl:px-12.5! xl:pt-10.75! xl:pb-25! 2xl:pt-12.5! 2xl:pb-28.75!"
    >
      <div class="relative mx-auto h-full max-w-360">
        <!-- Desktop: counter and arrow (Top Right) -->
        <div
          class="pointer-events-auto absolute top-0 right-0 hidden items-center justify-end gap-4 md:flex xl:min-w-31.5"
        >
          <span
            class="text-sm leading-[120%] font-normal md:text-[17px] xl:text-xl"
          >
            {{ currentIndex + 1 }} / {{ slides.length }}
          </span>
          <button
            @click.stop.prevent="nextSlide"
            :aria-label="t('common.next.slide')"
            class="relative z-10 flex h-6.5 w-6.5 cursor-pointer items-center justify-center rounded-full transition-opacity before:absolute before:-inset-4 before:content-[''] hover:opacity-80"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
              viewBox="0 0 26 26"
              fill="none"
            >
              <mask :id="`arrow-mask-next-${componentId}-static`">
                <rect width="26" height="26" fill="white" rx="13" />
                <path
                  d="M12 6.75 18.385 13 12 19.25M5.5 13h12"
                  stroke="black"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </mask>
              <circle
                cx="13"
                cy="13"
                r="13"
                fill="currentColor"
                :mask="`url(#arrow-mask-next-${componentId}-static)`"
              />
            </svg>
          </button>
        </div>

        <!-- Mobile: counter and arrow (Bottom Center) -->
        <div
          class="pointer-events-auto absolute bottom-0 left-1/2 flex -translate-x-1/2 items-center justify-center gap-4 md:hidden"
        >
          <span class="text-sm leading-[120%] font-normal">
            {{ currentIndex + 1 }} / {{ slides.length }}
          </span>
          <button
            @click.stop.prevent="nextSlide"
            :aria-label="t('common.next.slide')"
            class="relative z-10 flex h-6.5 w-6.5 cursor-pointer items-center justify-center rounded-full transition-opacity hover:opacity-80"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
              viewBox="0 0 26 26"
              fill="none"
            >
              <mask :id="`arrow-mask-mobile-${componentId}-static`">
                <rect width="26" height="26" fill="white" rx="13" />
                <path
                  d="M12 6.75 18.385 13 12 19.25M5.5 13h12"
                  stroke="black"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </mask>
              <circle
                cx="13"
                cy="13"
                r="13"
                fill="currentColor"
                :mask="`url(#arrow-mask-mobile-${componentId}-static)`"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <Swiper
      :slides-per-view="1"
      :loop="slides?.length > 1"
      :allow-touch-move="false"
      @swiper="onSwiperInit"
      @slideChange="onSlideChange"
      class="w-full"
    >
      <SwiperSlide
        v-for="(slide, index) in slides"
        :key="slide.id || index"
        v-slot="{ isActive }"
      >
        <component
          :is="slide.menu_url ? NuxtLink : 'div'"
          :to="localePath(slide.menu_url)"
          :class="[
            'block w-full px-5 pt-5 pb-10 transition-colors duration-500 md:px-7.5 md:pt-7.5 md:pb-22.5 xl:px-12.5 xl:pt-10.75 xl:pb-25 2xl:pt-12.5 2xl:pb-28.75',
            slide.menu_url ? 'cursor-pointer' : '',
          ]"
          :style="[
            {
              backgroundColor:
                slide.background &&
                slide.background !== 'white' &&
                slide.background !== '#ffffff' &&
                slide.background !== 'ffffff'
                  ? slide.background.startsWith('#')
                    ? slide.background
                    : `#${slide.background}`
                  : '#CDB946',
            },
          ]"
        >
          <div class="mx-auto max-w-360">
            <!-- Top bar: date + counter/arrow — md+ only -->
            <div
              class="relative mb-10 hidden min-h-5 w-full items-center justify-between md:flex lg:mb-11.75 xl:mb-11! 2xl:mb-11.75!"
            >
              <div
                :class="[
                  'flex transition-opacity duration-500',
                  isActive ? 'opacity-100 delay-100' : 'opacity-0',
                ]"
              >
                <span
                  v-if="slide.eventTags?.length || slide.type"
                  class="rounded-sm border border-current px-2 py-1.5 text-sm leading-[110%] xl:text-sm/[85%] 2xl:text-[17px]/[110%]"
                >
                  {{
                    slide.eventTags?.map((t: any) => t.name).join(', ') ||
                    slide.type
                  }}
                </span>
              </div>

              <div
                :class="[
                  'text-sm leading-[120%] transition-opacity duration-500 md:absolute md:left-1/2 md:-translate-x-1/2 md:text-[17px] xl:top-0 xl:z-20 xl:text-xl 2xl:text-[23px]/[29px]',
                  isActive ? 'opacity-100 delay-100' : 'opacity-0',
                ]"
              >
                <span class="hidden md:inline">{{ formattedDate }}</span>
              </div>

              <!-- Dummy invisible block to maintain layout spacing in the sliding content -->
              <div
                class="pointer-events-none flex items-center justify-center gap-4 opacity-0 xl:min-w-31.5"
              >
                <span
                  v-if="slides.length > 1"
                  class="text-sm leading-[120%] font-normal md:text-[17px] xl:text-xl"
                >
                  {{ index + 1 }} / {{ slides.length }}
                </span>
                <div v-if="slides.length > 1" class="h-6.5 w-6.5"></div>
              </div>
            </div>

            <!-- Mobile-only: date centered at top -->
            <div class="mb-10 text-center text-sm leading-[120%] md:hidden">
              {{ formattedDate }}
            </div>

            <EventCard
              :slide="slide"
              :is-standalone="false"
              :title-group-id="componentId"
              :fixed-height="
                maxTitleHeight !== 'auto' ? maxTitleHeight : undefined
              "
            />

            <!-- Dummy invisible block for Mobile spacing -->
            <div
              v-if="slides.length > 1"
              class="pointer-events-none mt-7.5 flex items-center justify-center gap-4 opacity-0 md:hidden"
            >
              <span class="text-sm leading-[120%] font-normal">
                {{ index + 1 }} / {{ slides.length }}
              </span>
              <div class="h-6.5 w-6.5"></div>
            </div>
          </div>
        </component>
      </SwiperSlide>
    </Swiper>
  </div>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { NuxtLink } from '#components'
import type { Swiper as SwiperClass } from 'swiper/types'
import type { AppEvent } from '~/types'

interface Props {
  slides: AppEvent[]
}

const props = defineProps<Props>()

const { locale, t } = useI18n()
const localePath = useLocalePath()

const componentId = useId()
const swiperInstance = ref<SwiperClass | null>(null)
const currentIndex = ref(0)
const maxTitleHeight = ref('auto')

const calculateMaxTitleHeight = () => {
  if (!props.slides || props.slides.length <= 1) {
    maxTitleHeight.value = 'auto'
    return
  }

  maxTitleHeight.value = 'auto'
  nextTick(() => {
    let max = 0
    const titles = document.querySelectorAll(
      `[data-slider-title="${componentId}"]`,
    )
    titles.forEach((el) => {
      const htmlEl = el as HTMLElement
      const saved = htmlEl.style.height
      htmlEl.style.height = 'auto'
      const h = htmlEl.getBoundingClientRect().height
      htmlEl.style.height = saved
      if (h > max) max = h
    })
    if (max > 0) maxTitleHeight.value = `${max}px`
  })
}

onMounted(() => {
  if (import.meta.client && document.fonts) {
    document.fonts.ready.then(() => calculateMaxTitleHeight())
  } else {
    calculateMaxTitleHeight()
  }

  window.addEventListener('resize', calculateMaxTitleHeight)
})

watch(
  () => props.slides,
  () => calculateMaxTitleHeight(),
  { deep: true },
)

onUnmounted(() => {
  if (import.meta.client) {
    window.removeEventListener('resize', calculateMaxTitleHeight)
  }
})

const onSwiperInit = (swiper: SwiperClass) => {
  swiperInstance.value = swiper
}

const onSlideChange = (swiper: SwiperClass) => {
  currentIndex.value = swiper.realIndex
}

const nextSlide = () => swiperInstance.value?.slideNext()

const formattedDate = useFormattedDate(
  () => props.slides?.[currentIndex.value]?.starts_at,
  () => props.slides?.[currentIndex.value]?.ends_at,
)
</script>
